/**
 * ProgramDetailPage - Detailansicht/Neuanlage eines Programms
 * 
 * Sektionen:
 * - Grunddaten (Nummer, Name)
 * - Technische Daten (Dos.Nr., Max.Temp, Max.Bel.%)
 * - Organisation (Programmtyp)
 * - Hinweise (Snapshot-Daten)
 * 
 * UX-Leitlinie: Kein Modal für Create/Edit, nur für Confirm
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ListChecks, ArrowLeft, RefreshCw, Save, Trash2, AlertTriangle } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';

type ProgramTypeColor = 'GREEN' | 'ORANGE' | 'PURPLE' | 'BLUE' | 'GRAY';

interface ProgramTypeEntity {
  id: string;
  code: string;
  name: string;
  color: ProgramTypeColor;
  isActive: boolean;
}

interface Program {
  id: string;
  code: string;
  number: string;
  name: string;
  programTypeId: string;
  programType?: ProgramTypeEntity;
  dosNumber: string | null;
  maxTemp: string | null;
  maxLoadPercent: number | null;
  remarks: string | null;
  isActive: boolean;
}

interface FormData {
  number: string;
  name: string;
  programTypeId: string;
  dosNumber: string;
  maxTemp: string;
  maxLoadPercent: number | null;
  remarks: string;
}

const INITIAL_FORM: FormData = {
  number: '',
  name: '',
  programTypeId: '',
  dosNumber: '',
  maxTemp: '',
  maxLoadPercent: null,
  remarks: '',
};

const COLOR_CLASSES: Record<ProgramTypeColor, string> = {
  GREEN: 'bg-green-100 text-green-800',
  ORANGE: 'bg-orange-100 text-orange-800',
  PURPLE: 'bg-purple-100 text-purple-800',
  BLUE: 'bg-blue-100 text-blue-800',
  GRAY: 'bg-gray-100 text-gray-800',
};

export default function ProgramDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';
  
  const [program, setProgram] = useState<Program | null>(null);
  const [programTypes, setProgramTypes] = useState<ProgramTypeEntity[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Delete Confirm State
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Load program types
  const loadProgramTypes = async () => {
    try {
      const res = await fetch('/api/admin/program-types', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setProgramTypes(data.filter((t: ProgramTypeEntity) => t.isActive));
      }
    } catch (err) {
      console.error('Fehler beim Laden der Programmtypen:', err);
    }
  };

  // Load program details (only for edit mode)
  const loadProgram = async () => {
    if (isNew || !id) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/programs/${id}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setProgram(data);
        setFormData({
          number: data.number,
          name: data.name,
          programTypeId: data.programTypeId || '',
          dosNumber: data.dosNumber || '',
          maxTemp: data.maxTemp || '',
          maxLoadPercent: data.maxLoadPercent,
          remarks: data.remarks || '',
        });
      } else if (res.status === 404) {
        navigate('/admin/programs');
      }
    } catch (err) {
      console.error('Fehler beim Laden des Programms:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadProgramTypes();
    if (!isNew) {
      loadProgram();
    }
  }, [id, isNew]);

  // Save (create or update)
  const handleSave = async () => {
    if (!formData.number.trim()) {
      setError('Programmnummer ist erforderlich');
      return;
    }
    if (!formData.name.trim()) {
      setError('Name ist erforderlich');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const url = isNew ? '/api/admin/programs' : `/api/admin/programs/${id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          number: formData.number.trim(),
          name: formData.name.trim(),
          programTypeId: formData.programTypeId || null,
          dosNumber: formData.dosNumber.trim() || null,
          maxTemp: formData.maxTemp.trim() || null,
          maxLoadPercent: formData.maxLoadPercent,
          remarks: formData.remarks.trim() || null,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        if (isNew) {
          navigate(`/admin/programs/${data.id}`);
        } else {
          loadProgram();
        }
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || data.message || 'Fehler beim Speichern');
      }
    } catch (err) {
      setError('Netzwerkfehler');
    }
    setSaving(false);
  };

  // Delete (soft delete)
  const handleDelete = async () => {
    if (!id || isNew) return;
    
    setDeleting(true);
    try {
      const res = await fetch(`/api/admin/programs/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (res.ok) {
        navigate('/admin/programs');
      }
    } catch (err) {
      console.error('Fehler beim Deaktivieren:', err);
    }
    setDeleting(false);
  };

  // Get program type by ID
  const getProgramType = (typeId: string) => programTypes.find(t => t.id === typeId);

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Laden...</div>
      </AdminLayout>
    );
  }

  if (!isNew && !program) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Programm nicht gefunden</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <Link 
              to="/admin/programs" 
              className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück zur Übersicht
            </Link>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <ListChecks className="w-6 h-6" />
              {isNew ? 'Neues Programm' : `Programm ${program?.number}`}
            </h1>
            {!isNew && program && (
              <p className="text-sm text-slate-500 mt-1">
                {program.name}
                {!program.isActive && <span className="ml-2 text-red-500">(inaktiv)</span>}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {!isNew && (
              <button
                onClick={() => loadProgram()}
                disabled={loading}
                className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                title="Aktualisieren"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving || !formData.number.trim() || !formData.name.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Form Sections */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Grunddaten */}
          <section className="bg-white rounded-lg border p-6 space-y-4">
            <h2 className="font-semibold text-slate-900 border-b pb-2">Grunddaten</h2>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Programmnummer <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.number}
                onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                placeholder="z.B. 01"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono"
                autoFocus
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="z.B. Kochwäsche"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </section>

          {/* Technische Daten */}
          <section className="bg-white rounded-lg border p-6 space-y-4">
            <h2 className="font-semibold text-slate-900 border-b pb-2">Technische Daten</h2>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Dosiernummer</label>
              <input
                type="text"
                value={formData.dosNumber}
                onChange={(e) => setFormData({ ...formData, dosNumber: e.target.value })}
                placeholder="z.B. D01"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Max. Temperatur</label>
              <input
                type="text"
                value={formData.maxTemp}
                onChange={(e) => setFormData({ ...formData, maxTemp: e.target.value })}
                placeholder="z.B. 95°C"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Max. Beladung %</label>
              <input
                type="number"
                value={formData.maxLoadPercent ?? ''}
                onChange={(e) => setFormData({ ...formData, maxLoadPercent: e.target.value ? parseInt(e.target.value) : null })}
                placeholder="z.B. 100"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </section>

          {/* Organisation */}
          <section className="bg-white rounded-lg border p-6 space-y-4">
            <h2 className="font-semibold text-slate-900 border-b pb-2">Organisation</h2>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Programmtyp</label>
              <select
                value={formData.programTypeId}
                onChange={(e) => setFormData({ ...formData, programTypeId: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Kein Typ</option>
                {programTypes.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              {formData.programTypeId && (
                <div className="mt-2">
                  {(() => {
                    const pt = getProgramType(formData.programTypeId);
                    return pt ? (
                      <span className={`inline-flex px-2 py-1 text-xs rounded ${COLOR_CLASSES[pt.color]}`}>
                        {pt.name}
                      </span>
                    ) : null;
                  })()}
                </div>
              )}
            </div>
          </section>

          {/* Anmerkungen */}
          <section className="bg-white rounded-lg border p-6 space-y-4">
            <h2 className="font-semibold text-slate-900 border-b pb-2">Anmerkungen</h2>
            
            <div>
              <textarea
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                placeholder="Optionale Anmerkungen zum Programm..."
                rows={4}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </section>

          {/* Hinweise */}
          <section className="bg-slate-50 rounded-lg border p-6 space-y-4 lg:col-span-2">
            <h2 className="font-semibold text-slate-700">Hinweise</h2>
            <p className="text-sm text-slate-600">
              Änderungen am Programm wirken sich nicht auf bereits erfasste Waschvorgänge aus. 
              Diese enthalten Snapshot-Daten zum Zeitpunkt der Erfassung.
            </p>
          </section>
        </div>

        {/* Delete Section (nur bei Edit) */}
        {!isNew && program && (
          <section className="bg-white rounded-lg border border-red-200 p-6">
            <h2 className="font-semibold text-red-600 mb-2">Gefahrenzone</h2>
            <p className="text-sm text-slate-600 mb-4">
              Das Deaktivieren eines Programms entfernt es aus der Auswahl bei der Erfassung. 
              Bereits erfasste Waschvorgänge bleiben unverändert.
            </p>
            <button
              onClick={() => setDeleteConfirmOpen(true)}
              className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Programm deaktivieren
            </button>
          </section>
        )}
      </div>

      {/* Delete Confirm Modal */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="p-4 border-b flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <h2 className="text-lg font-semibold text-red-600">Programm deaktivieren?</h2>
            </div>
            <div className="p-4">
              <p className="text-slate-600">
                <strong>"{program?.name}"</strong> (Nr. {program?.number}) wird deaktiviert.
              </p>
              <p className="text-sm text-slate-500 mt-2">
                Das Programm kann nicht mehr für neue Erfassungen verwendet werden.
              </p>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t bg-slate-50 rounded-b-xl">
              <button
                onClick={() => setDeleteConfirmOpen(false)}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100"
              >
                Abbrechen
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deaktivieren...' : 'Deaktivieren'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
